import { TypeOfWork } from '@pflegia/types/api/candidate/type-of-work';
import { LocationObject } from '@pflegia/types/common/location/location-object';
import { getLocationValue } from '@pflegia/util/common/location';
import { normalizeJobsParams } from '@pflegia/util/common/normalize-jobs-params';

import { JobTitleFilterValue } from '../application/use-job-titles-filter-list.hook';
import { FiltersDto } from '../dto/filters-dto';
import JobBoardPageProps from '../types/job-board-page';
import { PflegefachkraftRoleGroup } from './pflegefachkraft-role-group';

interface GetJobTitleSlugParams {
  jobTitle?: Pick<NonNullable<JobBoardPageProps['jobTitle']>, 'deputy' | 'title'> | null;
  translatedJobTitle?: string;
}

interface BuildPrimaryJobBoardSeoStateParams {
  basePath: string;
  defaultJobTitle?: JobBoardPageProps['jobTitle'];
  defaultJobTitleForSeo: string;
  defaultLocation?: LocationObject;
  defaultLocationForSeo: string;
  defaultTypeOfWork?: TypeOfWork;
  filtersDto: FiltersDto;
  jobTitles: JobTitleFilterValue[];
  pflegefachkraftRoleGroup?: Pick<PflegefachkraftRoleGroup, 'slug' | 'label'>;
  translateTypeOfWork: (typeOfWork: TypeOfWork) => string;
}

export interface PrimaryJobBoardSeoState {
  canSyncRoute: boolean;
  jobTitle?: JobBoardPageProps['jobTitle'];
  jobTitleForSeo: string;
  listingPath: string;
  locationForSeo: string;
  locationObject?: LocationObject;
  typeOfWork?: TypeOfWork;
}

const getJobTitleSlug = ({ jobTitle, translatedJobTitle }: GetJobTitleSlugParams) => {
  if (!jobTitle) return normalizeJobsParams(translatedJobTitle);

  return normalizeJobsParams(translatedJobTitle || jobTitle.title.replaceAll('_', ' '));
};

export const buildPrimaryJobBoardSeoState = ({
  basePath,
  defaultJobTitle,
  defaultJobTitleForSeo,
  defaultLocation,
  defaultLocationForSeo,
  defaultTypeOfWork,
  filtersDto,
  jobTitles,
  pflegefachkraftRoleGroup,
  translateTypeOfWork,
}: BuildPrimaryJobBoardSeoStateParams): PrimaryJobBoardSeoState => {
  const [primaryPositionFilter] = filtersDto.getPositionFilters();
  const [primaryLocationFilter] = filtersDto.getLocationFilters();
  const [primaryFacilityFilter] = filtersDto.getFacilityFilters();
  const primaryJobTitleOption = jobTitles.find(({ value }) => value === primaryPositionFilter?.value);
  const resolvedJobTitle = pflegefachkraftRoleGroup
    ? undefined
    : ((primaryJobTitleOption?.jobTitle as JobBoardPageProps['jobTitle']) ?? defaultJobTitle);
  const resolvedJobTitleForSeo = pflegefachkraftRoleGroup?.label ?? (primaryJobTitleOption?.text || defaultJobTitleForSeo);
  const resolvedLocationObject = primaryLocationFilter?.locationObject ?? defaultLocation;
  const resolvedLocationForSeo = getLocationValue(resolvedLocationObject) || defaultLocationForSeo;
  const resolvedTypeOfWork = (primaryFacilityFilter?.value as TypeOfWork | undefined) ?? defaultTypeOfWork;
  const jobTitleSlug =
    pflegefachkraftRoleGroup?.slug ??
    getJobTitleSlug({
      jobTitle: resolvedJobTitle,
      translatedJobTitle: resolvedJobTitleForSeo,
    });
  const listingPath = [
    basePath,
    jobTitleSlug,
    normalizeJobsParams(resolvedLocationForSeo),
    resolvedTypeOfWork ? normalizeJobsParams(translateTypeOfWork(resolvedTypeOfWork)) : undefined,
  ]
    .filter(Boolean)
    .join('/');

  return {
    canSyncRoute: !primaryPositionFilter || Boolean(jobTitleSlug),
    jobTitle: resolvedJobTitle,
    jobTitleForSeo: resolvedJobTitleForSeo,
    listingPath,
    locationForSeo: resolvedLocationForSeo,
    locationObject: resolvedLocationObject,
    typeOfWork: resolvedTypeOfWork,
  };
};
